/************************************************************************************************************
Author                          : Trizent Technologies Private Limited
Date                            : 
Notes/Remarks                   : 

Organization:  	Trizent Technologies Private Limited.
		Module No. 17, SBC, Thejaswini Building
		Phase 1, Technopark, Thiruvananthapuram - 695581, 
		Kerala, India
 
Copyright 2026 (C) Trizent Technologies Pvt Ltd (www.trizentinc.com)
All rights reserved.
***************************************************************************************************************/

import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import HeaderSection from '../../common/headerSection';
import PageHeader from '../../common/pageHeader';
import { MLTTestRecord, INITIAL_MLT_RECORDS } from './data/mltBioburdenData';

export const MLTBioburdenDetailedReport: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  
  // Get record from route state or fallback
  const record: MLTTestRecord = location.state?.record || 
    INITIAL_MLT_RECORDS.find(r => r.reportAvailable) || 
    INITIAL_MLT_RECORDS[0];

  const [toastMessage, setToastMessage] = useState<string | null>(null);

  const showToast = (msg: string) => {
    setToastMessage(msg);
    setTimeout(() => setToastMessage(null), 3500);
  };

  const handlePrint = () => {
    window.print();
  };

  const handleExportPDF = () => {
    showToast('Preparing PDF document for export...');
    setTimeout(() => {
      window.print();
    }, 400);
  };

  const handleBack = () => {
    navigate('/mltBioburdenTests');
  };

  const getDecisionBadge = (decision: string) => {
    switch (decision) {
      case 'Approved':
        return 'bg-emerald-100 text-emerald-900 border-emerald-300';
      case 'Rejected':
        return 'bg-rose-100 text-rose-900 border-rose-300';
      case 'To Be Reprocessed':
        return 'bg-amber-100 text-amber-900 border-amber-300';
      default:
        return 'bg-slate-100 text-slate-800 border-slate-300';
    }
  };

  const getCompliancePill = (status: string) => {
    switch (status) {
      case 'Complies':
      case 'Compliant':
        return 'bg-emerald-50 text-emerald-800 border-emerald-200';
      case 'Out of Specification':
      case 'Does Not Comply':
      case 'Non-Compliant':
        return 'bg-rose-50 text-rose-800 border-rose-200';
      case 'Pending':
        return 'bg-amber-50 text-amber-800 border-amber-200';
      default:
        return 'bg-slate-100 text-slate-600 border-slate-200';
    }
  };

  return (
    <div>
      <HeaderSection />
      <PageHeader pageHeader={t('navbar.Reports.MLT Bioburden Tests')} pageDescription="Detailed Analytical Report" />
      
      <div className="min-h-screen bg-[#F8FAFC] text-slate-800 pb-20 print:bg-white print:p-0 print:pb-0">
        {/* Print-specific style overrides */}
        <style>{`
        @media print {
          @page {
            size: A4 portrait;
            margin: 12mm 15mm 15mm 15mm;
          }
          body {
            background-color: #ffffff !important;
            color: #000000 !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          .no-print {
            display: none !important;
          }
          .print-break-inside-avoid {
            break-inside: avoid;
            page-break-inside: avoid;
          }
          .print-table {
            border-collapse: collapse !important;
            width: 100% !important;
          }
          .print-table th, .print-table td {
            border: 1px solid #cbd5e1 !important;
          }
          header, aside, nav, .sticky {
            position: static !important;
          }
        }
      `}</style>

        {/* Floating Toast Notification */}
        {toastMessage && (
          <div className="fixed top-4 right-6 z-50 bg-[#0F2942] text-white px-4 py-2.5 rounded-xl shadow-xl text-xs font-bold flex items-center gap-2 border border-slate-700 no-print animate-fade-in">
            <span className="material-symbols-outlined text-sm text-blue-400">info</span>
            <span>{toastMessage}</span>
          </div>
        )}

        {/* Zent EM Top Page Action Bar (Hidden on print) */}
        <header className="bg-white border-b border-slate-200 shadow-xs sticky top-0 z-30 no-print">
          <div className="max-w-[1720px] mx-auto px-6 py-3.5">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div>
                {/* Breadcrumb */}
                <nav className="flex items-center gap-1.5 text-[11px] font-semibold text-slate-400 uppercase tracking-wider">
                  <span className="hover:text-slate-600 cursor-pointer" onClick={handleBack}>MLT Bioburden Tests</span>
                  <span>/</span>
                  <span className="text-[#0052CC] font-bold">Detailed Report</span>
                </nav>

                <div className="flex items-center gap-3 mt-1">
                  <h1 className="text-xl font-black text-[#0F2942] tracking-tight">
                    MLT Bioburden Analytical Report
                  </h1>
                  <span className="px-2 py-0.5 bg-blue-50 text-[#0052CC] border border-blue-200 rounded text-[11px] font-mono font-bold">
                    {record.arNumber}
                  </span>
                  <span className={`px-2 py-0.5 rounded text-[10px] font-black uppercase tracking-wider border ${
                    record.status === 'Completed' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                    record.status === 'Rejected' ? 'bg-rose-50 text-rose-700 border-rose-200' :
                    'bg-blue-50 text-blue-700 border-blue-200'
                  }`}>
                    {record.status}
                  </span>
                </div>
              </div>

              {/* Header Right Action Buttons */}
              <div className="flex items-center gap-2.5">
                <button
                  type="button"
                  onClick={handleBack}
                  className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 border border-slate-200"
                >
                  <span className="material-symbols-outlined text-base">arrow_back</span>
                  Back to Listing
                </button>

                <button
                  type="button"
                  onClick={handlePrint}
                  className="px-4 py-2 bg-white hover:bg-slate-50 text-slate-700 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 border border-slate-300 shadow-xs"
                >
                  <span className="material-symbols-outlined text-base">print</span>
                  Print Report
                </button>

                <button
                  type="button"
                  onClick={handleExportPDF}
                  className="px-4 py-2 bg-[#0052CC] hover:bg-blue-700 text-white rounded-lg text-xs font-black uppercase tracking-wider transition-all flex items-center gap-1.5 shadow-xs"
                >
                  <span className="material-symbols-outlined text-base">picture_as_pdf</span>
                  Export PDF
                </button>
              </div>
            </div>
          </div>
        </header>

        {/* Main Continuous Analytical Report Document */}
        <main className="max-w-[1280px] mx-auto px-6 pt-6 print:max-w-full print:p-0">
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-8 print:p-0 print:border-none print:shadow-none space-y-8">
          {/* SANGROSE / ZENT EM OFFICIAL CERTIFICATE OF ANALYSIS HEADER */}
          <div className="border-b-2 border-[#0F2942] pb-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-xl bg-[#0052CC] text-white flex items-center justify-center font-black text-xl tracking-wider shrink-0 shadow-xs">
                  <span className="material-symbols-outlined text-3xl">biotech</span>
                </div>
                <div>
                  <h2 className="text-lg font-black text-[#0F2942] tracking-tight uppercase">
                    Pharmaceutical Quality Control Laboratories
                  </h2>
                  <p className="text-xs text-slate-500 font-medium">
                    Quality Control Microbiology Testing Division • GLP / cGMP Certified Laboratory
                  </p>
                  <p className="text-[10px] text-slate-400 font-mono mt-0.5">
                    Doc ID: SOP-QC-REP-022 • 21 CFR Part 211.194 / USP &lt;61&gt; &amp; &lt;62&gt; Compliant
                  </p>
                </div>
              </div>

              <div className="text-left sm:text-right font-mono text-xs space-y-1">
                <div className="text-[10px] font-black uppercase tracking-wider text-slate-400">
                  Analytical Report Certificate
                </div>
                <div className="text-sm font-black text-[#0052CC] bg-blue-50 px-2.5 py-1 rounded border border-blue-200 inline-block">
                  {record.arNumber}
                </div>
                <div className="text-[11px] text-slate-500">
                  Report Date: <strong>{record.reportedOn || new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</strong>
                </div>
              </div>
            </div>

            <div className="mt-4 pt-3 border-t border-slate-100 flex flex-wrap items-center justify-between gap-4 text-xs">
              <div className="flex items-center gap-6">
                <div>
                  <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest block">TEST ID:</span>
                  <span className="font-mono font-bold text-slate-800">{record.id}</span>
                </div>
                <div>
                  <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest block">TASK ID:</span>
                  <span className="font-mono font-bold text-slate-800">{record.taskId}</span>
                </div>
                <div>
                  <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest block">TEST METHOD:</span>
                  <span className="font-bold text-indigo-700">{record.testMethod}</span>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">FINAL DECISION:</span>
                <span className={`px-3 py-1 rounded-md text-xs font-black uppercase tracking-wider border ${getDecisionBadge(record.overallCompliance.finalDecision)}`}>
                  {record.overallCompliance.finalDecision}
                </span>
              </div>
            </div>
          </div>

          {/* ============================================================== */}
          {/* SECTION 1: ANALYTICAL REPORT DETAILS (Compact Row-Wise Table) */}
          {/* ============================================================== */}
          <section className="print-break-inside-avoid">
            <div className="flex items-center gap-2 mb-3">
              <span className="w-5 h-5 rounded-md bg-[#0F2942] text-white flex items-center justify-center text-[11px] font-black">
                1
              </span>
              <h3 className="text-xs font-black text-[#0F2942] uppercase tracking-wider">
                Analytical Report Details
              </h3>
            </div>

            <div className="border border-slate-300 rounded-xl overflow-hidden shadow-2xs">
              <table className="w-full text-xs text-left border-collapse print-table">
                <tbody>
                  <tr className="border-b border-slate-200">
                    <th className="py-2.5 px-3.5 bg-slate-100/90 font-bold text-slate-700 w-1/4 border-r border-slate-200">
                      Analytical Report Number
                    </th>
                    <td className="py-2.5 px-3.5 font-mono font-bold text-[#0052CC] w-1/4 border-r border-slate-200">
                      {record.arNumber}
                    </td>
                    <th className="py-2.5 px-3.5 bg-slate-100/90 font-bold text-slate-700 w-1/4 border-r border-slate-200">
                      A.R. Number / Protocol Ref
                    </th>
                    <td className="py-2.5 px-3.5 font-mono text-slate-800 w-1/4">
                      {record.arNumber}
                    </td>
                  </tr>

                  <tr className="border-b border-slate-200">
                    <th className="py-2.5 px-3.5 bg-slate-100/90 font-bold text-slate-700 border-r border-slate-200">
                      Task ID / Test ID
                    </th>
                    <td className="py-2.5 px-3.5 font-mono text-slate-800 border-r border-slate-200">
                      {record.taskId} / {record.id}
                    </td>
                    <th className="py-2.5 px-3.5 bg-slate-100/90 font-bold text-slate-700 border-r border-slate-200">
                      Sample ID
                    </th>
                    <td className="py-2.5 px-3.5 font-mono font-bold text-slate-800">
                      {record.sampleId}
                    </td>
                  </tr>

                  <tr className="border-b border-slate-200">
                    <th className="py-2.5 px-3.5 bg-slate-100/90 font-bold text-slate-700 border-r border-slate-200">
                      Sample Type
                    </th>
                    <td className="py-2.5 px-3.5 font-bold text-slate-800 border-r border-slate-200">
                      {record.sampleType}
                    </td>
                    <th className="py-2.5 px-3.5 bg-slate-100/90 font-bold text-slate-700 border-r border-slate-200">
                      Sample Subtype
                    </th>
                    <td className="py-2.5 px-3.5 text-slate-700 font-medium">
                      {record.sampleSubtype}
                    </td>
                  </tr>

                  <tr className="border-b border-slate-200">
                    <th className="py-2.5 px-3.5 bg-slate-100/90 font-bold text-slate-700 border-r border-slate-200">
                      Material / Product Name
                    </th>
                    <td className="py-2.5 px-3.5 font-bold text-slate-900 border-r border-slate-200">
                      {record.sampleName}
                      {record.productCode && (
                        <span className="block text-[10px] text-slate-400 font-mono font-normal">
                          Code: {record.productCode}
                        </span>
                      )}
                    </td>
                    <th className="py-2.5 px-3.5 bg-slate-100/90 font-bold text-slate-700 border-r border-slate-200">
                      Production Batch / Lot No.
                    </th>
                    <td className="py-2.5 px-3.5 font-mono font-bold text-slate-900">
                      {record.batchNo}
                      {record.productionSchedule && (
                        <span className="block text-[10px] text-slate-400 font-sans font-normal">
                          (Sched: {record.productionSchedule})
                        </span>
                      )}
                    </td>
                  </tr>

                  <tr className="border-b border-slate-200">
                    <th className="py-2.5 px-3.5 bg-slate-100/90 font-bold text-slate-700 border-r border-slate-200">
                      Quantity Sampled / Retention
                    </th>
                    <td className="py-2.5 px-3.5 text-slate-800 border-r border-slate-200">
                      {record.quantitySampled} / {record.retentionSampleQuantity}
                    </td>
                    <th className="py-2.5 px-3.5 bg-slate-100/90 font-bold text-slate-700 border-r border-slate-200">
                      Qty Used for Test / Destroyed
                    </th>
                    <td className="py-2.5 px-3.5 text-slate-800">
                      {record.quantityUsedForTest} / {record.quantityDestroyed}
                    </td>
                  </tr>

                  <tr className="border-b border-slate-200">
                    <th className="py-2.5 px-3.5 bg-slate-100/90 font-bold text-slate-700 border-r border-slate-200">
                      Sampling Date &amp; Location
                    </th>
                    <td className="py-2.5 px-3.5 text-slate-800 border-r border-slate-200">
                      {record.samplingDate}
                      <span className="block text-[10px] text-slate-500 font-medium">
                        Loc: {record.samplingLocation}
                      </span>
                    </td>
                    <th className="py-2.5 px-3.5 bg-slate-100/90 font-bold text-slate-700 border-r border-slate-200">
                      Registration No. &amp; Date
                    </th>
                    <td className="py-2.5 px-3.5 text-slate-800">
                      <span className="font-mono">{record.registrationNumber}</span>
                      <span className="block text-[10px] text-slate-500 font-medium">{record.registrationDate}</span>
                    </td>
                  </tr>

                  <tr className="border-b border-slate-200">
                    <th className="py-2.5 px-3.5 bg-slate-100/90 font-bold text-slate-700 border-r border-slate-200">
                      Configured Test Method
                    </th>
                    <td className="py-2.5 px-3.5 font-bold text-indigo-700 border-r border-slate-200">
                      {record.testMethod}
                    </td>
                    <th className="py-2.5 px-3.5 bg-slate-100/90 font-bold text-slate-700 border-r border-slate-200">
                      Pretreatment SOP Reference
                    </th>
                    <td className="py-2.5 px-3.5 font-mono text-[11px] text-slate-700">
                      {record.samplePretreatmentRef}
                    </td>
                  </tr>

                  <tr>
                    <th className="py-2.5 px-3.5 bg-slate-100/90 font-bold text-slate-700 border-r border-slate-200">
                      Tested By / Checked By
                    </th>
                    <td className="py-2.5 px-3.5 text-slate-800 border-r border-slate-200">
                      {record.testedByAnalyst} / {record.checkedBySupervisor}
                    </td>
                    <th className="py-2.5 px-3.5 bg-slate-100/90 font-bold text-slate-700 border-r border-slate-200">
                      Total Pages in Protocol
                    </th>
                    <td className="py-2.5 px-3.5 text-slate-800 font-medium">
                      {record.totalPages}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          {/* ====================================================================== */}
          {/* SECTION 2: SAMPLE PRETREATMENT AND TEST CONDITIONS                      */}
          {/* ====================================================================== */}
          <section className="print-break-inside-avoid">
            <div className="flex items-center gap-2 mb-3">
              <span className="w-5 h-5 rounded-md bg-[#0F2942] text-white flex items-center justify-center text-[11px] font-black">
                2
              </span>
              <h3 className="text-xs font-black text-[#0F2942] uppercase tracking-wider">
                Sample Pretreatment &amp; Test Preparation Conditions
              </h3>
            </div>

            <div className="border border-slate-300 rounded-xl overflow-hidden p-4 bg-slate-50/50 space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-xs">
                <div>
                  <span className="text-[10px] font-black text-slate-400 uppercase tracking-wider block">Quantity Used</span>
                  <span className="font-bold text-slate-800">{record.prepQuantityUsed}</span>
                </div>
                <div>
                  <span className="text-[10px] font-black text-slate-400 uppercase tracking-wider block">Dilution Series</span>
                  <span className="font-bold text-slate-800">{record.dilutionSeries} (Factor: {record.dilutionFactor})</span>
                </div>
                <div>
                  <span className="text-[10px] font-black text-slate-400 uppercase tracking-wider block">Neutralizer Used</span>
                  <span className="font-bold text-slate-800">{record.neutralizer}</span>
                </div>
                <div>
                  <span className="text-[10px] font-black text-slate-400 uppercase tracking-wider block">Method Applied</span>
                  <span className="font-bold text-indigo-700">{record.testMethod}</span>
                </div>
              </div>

              <div className="pt-2 border-t border-slate-200 text-xs">
                <span className="text-[10px] font-black text-slate-400 uppercase tracking-wider block">
                  Detailed Pretreatment Protocol:
                </span>
                <p className="text-slate-800 font-medium mt-0.5 leading-relaxed">
                  {record.samplePretreatment}
                </p>
              </div>
            </div>
          </section>

          {/* ====================================================================== */}
          {/* SECTION 3: TOTAL AEROBIC COUNT (TAC)                                   */}
          {/* ====================================================================== */}
          <section className="print-break-inside-avoid">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <span className="w-5 h-5 rounded-md bg-[#0F2942] text-white flex items-center justify-center text-[11px] font-black">
                  3
                </span>
                <h3 className="text-xs font-black text-[#0F2942] uppercase tracking-wider">
                  Total Aerobic Microbial Count (TAMC / TAC)
                </h3>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-[11px] font-bold text-slate-600">Calculated Count:</span>
                <span className="font-mono font-bold text-blue-700 bg-blue-50 px-2 py-0.5 rounded border border-blue-200 text-xs">
                  {record.tacCalculatedResult}
                </span>
                <span className={`px-2 py-0.5 rounded text-[10px] font-black uppercase tracking-wider border ${getCompliancePill(record.tacComplianceStatus)}`}>
                  {record.tacComplianceStatus}
                </span>
              </div>
            </div>

            {/* Test Conditions Banner */}
            <div className="bg-slate-50 border border-slate-200 rounded-t-xl p-3 grid grid-cols-1 md:grid-cols-4 gap-3 text-xs mb-[-1px]">
              <div>
                <span className="text-[10px] font-black text-slate-400 uppercase tracking-wider block">Culture Media</span>
                <span className="font-bold text-slate-800">{record.tacMediaName}</span>
              </div>
              <div>
                <span className="text-[10px] font-black text-slate-400 uppercase tracking-wider block">Test Method</span>
                <span className="font-bold text-slate-800">{record.tacTestMethod}</span>
              </div>
              <div>
                <span className="text-[10px] font-black text-slate-400 uppercase tracking-wider block">Incubation Conditions</span>
                <span className="font-bold text-slate-800">{record.tacIncubationConditions}</span>
              </div>
              <div>
                <span className="text-[10px] font-black text-slate-400 uppercase tracking-wider block">Specification Limit</span>
                <span className="font-bold text-slate-900 font-mono">{record.tacSpecLimit}</span>
              </div>
            </div>

            {/* Plate Results Table */}
            <div className="border border-slate-300 rounded-b-xl overflow-hidden">
              <table className="w-full text-left text-xs border-collapse print-table">
                <thead>
                  <tr className="bg-slate-100 border-b border-slate-200 text-[10px] font-black text-slate-600 uppercase tracking-wider">
                    <th className="py-2.5 px-3 text-center">S. No.</th>
                    <th className="py-2.5 px-3">Plate No.</th>
                    <th className="py-2.5 px-3">Plate Serial No.</th>
                    <th className="py-2.5 px-3">Media Type</th>
                    <th className="py-2.5 px-3">Dilution</th>
                    <th className="py-2.5 px-3">Volume</th>
                    <th className="py-2.5 px-3 text-center">Colony Count (CFU)</th>
                    <th className="py-2.5 px-3 text-center">Calculated Count</th>
                    <th className="py-2.5 px-4">Remarks</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 font-medium">
                  {record.tacPlates.length === 0 ? (
                    <tr>
                      <td colSpan={9} className="py-4 text-center text-slate-400 italic">
                        No TAC plate observations recorded yet.
                      </td>
                    </tr>
                  ) : (
                    record.tacPlates.map((p: any) => (
                      <tr key={p.sNo} className="hover:bg-slate-50/60">
                        <td className="py-2 px-3 text-center font-mono text-slate-500">{p.sNo}</td>
                        <td className="py-2 px-3 font-bold text-slate-800">{p.plateNo}</td>
                        <td className="py-2 px-3 font-mono font-bold text-[#0052CC]">{p.serialNo}</td>
                        <td className="py-2 px-3 text-slate-700">{p.mediaType}</td>
                        <td className="py-2 px-3 text-slate-600">{p.dilution}</td>
                        <td className="py-2 px-3 text-slate-600">{p.volume}</td>
                        <td className="py-2 px-3 text-center font-bold text-slate-900">{p.cfu}</td>
                        <td className="py-2 px-3 text-center font-mono font-bold text-blue-700">{p.calculatedCount}</td>
                        <td className="py-2 px-4 text-slate-600 text-[11px]">{p.remarks}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </section>

          {/* ====================================================================== */}
          {/* SECTION 4: TOTAL FUNGAL COUNT (TFC / TYMC)                             */}
          {/* Continuous on the same page - no tabs!                                */}
          {/* ====================================================================== */}
          <section className="print-break-inside-avoid">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <span className="w-5 h-5 rounded-md bg-[#0F2942] text-white flex items-center justify-center text-[11px] font-black">
                  4
                </span>
                <h3 className="text-xs font-black text-[#0F2942] uppercase tracking-wider">
                  Total Yeast &amp; Mold Count (TYMC / TFC)
                </h3>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-[11px] font-bold text-slate-600">Calculated Count:</span>
                <span className="font-mono font-bold text-blue-700 bg-blue-50 px-2 py-0.5 rounded border border-blue-200 text-xs">
                  {record.tfcCalculatedResult}
                </span>
                <span className={`px-2 py-0.5 rounded text-[10px] font-black uppercase tracking-wider border ${getCompliancePill(record.tfcComplianceStatus)}`}>
                  {record.tfcComplianceStatus}
                </span>
              </div>
            </div>

            {/* Test Conditions Banner */}
            <div className="bg-slate-50 border border-slate-200 rounded-t-xl p-3 grid grid-cols-1 md:grid-cols-4 gap-3 text-xs mb-[-1px]">
              <div>
                <span className="text-[10px] font-black text-slate-400 uppercase tracking-wider block">Culture Media</span>
                <span className="font-bold text-slate-800">{record.tfcMediaName}</span>
              </div>
              <div>
                <span className="text-[10px] font-black text-slate-400 uppercase tracking-wider block">Test Method</span>
                <span className="font-bold text-slate-800">{record.tfcTestMethod}</span>
              </div>
              <div>
                <span className="text-[10px] font-black text-slate-400 uppercase tracking-wider block">Incubation Conditions</span>
                <span className="font-bold text-slate-800">{record.tfcIncubationConditions}</span>
              </div>
              <div>
                <span className="text-[10px] font-black text-slate-400 uppercase tracking-wider block">Specification Limit</span>
                <span className="font-bold text-slate-900 font-mono">{record.tfcSpecLimit}</span>
              </div>
            </div>

            {/* Plate Results Table */}
            <div className="border border-slate-300 rounded-b-xl overflow-hidden">
              <table className="w-full text-left text-xs border-collapse print-table">
                <thead>
                  <tr className="bg-slate-100 border-b border-slate-200 text-[10px] font-black text-slate-600 uppercase tracking-wider">
                    <th className="py-2.5 px-3 text-center">S. No.</th>
                    <th className="py-2.5 px-3">Plate No.</th>
                    <th className="py-2.5 px-3">Plate Serial No.</th>
                    <th className="py-2.5 px-3">Media Type</th>
                    <th className="py-2.5 px-3">Dilution</th>
                    <th className="py-2.5 px-3">Volume</th>
                    <th className="py-2.5 px-3 text-center">Colony Count (CFU)</th>
                    <th className="py-2.5 px-3 text-center">Calculated Count</th>
                    <th className="py-2.5 px-4">Remarks</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 font-medium">
                  {record.tfcPlates.length === 0 ? (
                    <tr>
                      <td colSpan={9} className="py-4 text-center text-slate-400 italic">
                        No TFC plate observations recorded yet.
                      </td>
                    </tr>
                  ) : (
                    record.tfcPlates.map((p: any) => (
                      <tr key={p.sNo} className="hover:bg-slate-50/60">
                        <td className="py-2 px-3 text-center font-mono text-slate-500">{p.sNo}</td>
                        <td className="py-2 px-3 font-bold text-slate-800">{p.plateNo}</td>
                        <td className="py-2 px-3 font-mono font-bold text-[#0052CC]">{p.serialNo}</td>
                        <td className="py-2 px-3 text-slate-700">{p.mediaType}</td>
                        <td className="py-2 px-3 text-slate-600">{p.dilution}</td>
                        <td className="py-2 px-3 text-slate-600">{p.volume}</td>
                        <td className="py-2 px-3 text-center font-bold text-slate-900">{p.cfu}</td>
                        <td className="py-2 px-3 text-center font-mono font-bold text-blue-700">{p.calculatedCount}</td>
                        <td className="py-2 px-4 text-slate-600 text-[11px]">{p.remarks}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </section>

          {/* ====================================================================== */}
          {/* SECTION 5: SPECIFIED ORGANISM TESTING (USP <62> / EP 2.6.13)          */}
          {/* ====================================================================== */}
          <section className="print-break-inside-avoid">
            <div className="flex items-center gap-2 mb-3">
              <span className="w-5 h-5 rounded-md bg-[#0F2942] text-white flex items-center justify-center text-[11px] font-black">
                5
              </span>
              <h3 className="text-xs font-black text-[#0F2942] uppercase tracking-wider">
                Specified Microbial Organism Testing (Tests for Specified Microorganisms)
              </h3>
            </div>

            <div className="border border-slate-300 rounded-xl overflow-hidden shadow-2xs">
              <table className="w-full text-left text-xs border-collapse print-table">
                <thead>
                  <tr className="bg-slate-100 border-b border-slate-200 text-[10px] font-black text-slate-600 uppercase tracking-wider">
                    <th className="py-2.5 px-2.5 text-center">S. No.</th>
                    <th className="py-2.5 px-3">Organism Name</th>
                    <th className="py-2.5 px-3">Test Method / Monograph</th>
                    <th className="py-2.5 px-3">Culture Medium</th>
                    <th className="py-2.5 px-3">Incubation Conditions</th>
                    <th className="py-2.5 px-3">Observation</th>
                    <th className="py-2.5 px-2.5 text-center">Result</th>
                    <th className="py-2.5 px-2.5 text-center">Compliance</th>
                    <th className="py-2.5 px-3">Analyst Sign-off</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 font-medium">
                  {record.specifiedOrganisms.length === 0 ? (
                    <tr>
                      <td colSpan={9} className="py-4 text-center text-slate-400 italic">
                        No specified organism testing configured or required for this test protocol.
                      </td>
                    </tr>
                  ) : (
                    record.specifiedOrganisms.map((org: any) => (
                      <tr key={org.sNo} className="hover:bg-slate-50/60">
                        <td className="py-2.5 px-2.5 text-center font-mono text-slate-500">{org.sNo}</td>
                        <td className="py-2.5 px-3 font-bold text-slate-900 italic">
                          {org.organismName}
                        </td>
                        <td className="py-2.5 px-3 text-slate-600 text-[11px]">{org.testMethod}</td>
                        <td className="py-2.5 px-3 text-slate-700">{org.cultureMedium}</td>
                        <td className="py-2.5 px-3 text-slate-600 text-[11px]">{org.incubationConditions}</td>
                        <td className="py-2.5 px-3 text-slate-700 text-[11px] leading-tight">{org.observation}</td>
                        <td className="py-2.5 px-2.5 text-center">
                          <span className={`px-2 py-0.5 rounded text-[10px] font-black uppercase ${
                            org.result === 'Absent' ? 'text-emerald-700 bg-emerald-50' :
                            org.result === 'Present' ? 'text-rose-700 bg-rose-50' : 'text-slate-600'
                          }`}>
                            {org.result}
                          </span>
                        </td>
                        <td className="py-2.5 px-2.5 text-center">
                          <span className={`px-2 py-0.5 rounded text-[10px] font-black uppercase tracking-wider border ${getCompliancePill(org.complianceStatus)}`}>
                            {org.complianceStatus}
                          </span>
                        </td>
                        <td className="py-2.5 px-3 text-slate-600 text-[11px] font-mono">
                          {org.testedBy}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </section>

          {/* ====================================================================== */}
          {/* SECTION 6: REMARKS (Analyst, Reviewer, QA)                             */}
          {/* ====================================================================== */}
          <section className="print-break-inside-avoid">
            <div className="flex items-center gap-2 mb-3">
              <span className="w-5 h-5 rounded-md bg-[#0F2942] text-white flex items-center justify-center text-[11px] font-black">
                6
              </span>
              <h3 className="text-xs font-black text-[#0F2942] uppercase tracking-wider">
                Laboratory Remarks &amp; Technical Observations
              </h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Analyst Remarks */}
              <div className="border border-slate-300 rounded-xl p-3.5 bg-slate-50/50 space-y-1.5">
                <div className="flex items-center justify-between border-b border-slate-200 pb-1">
                  <span className="text-[10px] font-black text-slate-500 uppercase tracking-wider">
                    Analyst Remarks
                  </span>
                  <span className="text-[9px] text-slate-400 font-mono">
                    {record.remarksSection.analystRemarks.timestamp}
                  </span>
                </div>
                <p className="text-xs text-slate-800 font-medium leading-relaxed">
                  {record.remarksSection.analystRemarks.text}
                </p>
                <span className="text-[10px] text-[#0052CC] font-bold block pt-1">
                  — {record.remarksSection.analystRemarks.author}
                </span>
              </div>

              {/* Reviewer Remarks */}
              <div className="border border-slate-300 rounded-xl p-3.5 bg-slate-50/50 space-y-1.5">
                <div className="flex items-center justify-between border-b border-slate-200 pb-1">
                  <span className="text-[10px] font-black text-slate-500 uppercase tracking-wider">
                    Supervisor / Reviewer Remarks
                  </span>
                  <span className="text-[9px] text-slate-400 font-mono">
                    {record.remarksSection.reviewerRemarks?.timestamp || 'Pending'}
                  </span>
                </div>
                <p className="text-xs text-slate-800 font-medium leading-relaxed">
                  {record.remarksSection.reviewerRemarks?.text || 'Awaiting supervisor secondary review verification.'}
                </p>
                <span className="text-[10px] text-purple-700 font-bold block pt-1">
                  — {record.remarksSection.reviewerRemarks?.author || 'Pending QC Supervisor'}
                </span>
              </div>

              {/* QA Remarks */}
              <div className="border border-slate-300 rounded-xl p-3.5 bg-slate-50/50 space-y-1.5">
                <div className="flex items-center justify-between border-b border-slate-200 pb-1">
                  <span className="text-[10px] font-black text-slate-500 uppercase tracking-wider">
                    Quality Assurance Remarks
                  </span>
                  <span className="text-[9px] text-slate-400 font-mono">
                    {record.remarksSection.qaRemarks?.timestamp || 'Pending'}
                  </span>
                </div>
                <p className="text-xs text-slate-800 font-medium leading-relaxed">
                  {record.remarksSection.qaRemarks?.text || 'Final QA lot disposition audit pending.'}
                </p>
                <span className="text-[10px] text-emerald-800 font-bold block pt-1">
                  — {record.remarksSection.qaRemarks?.author || 'Pending QA Head'}
                </span>
              </div>
            </div>
          </section>

          {/* ====================================================================== */}
          {/* SECTION 7: OVERALL COMPLIANCE AND FINAL DECISION                       */}
          {/* ====================================================================== */}
          <section className="print-break-inside-avoid">
            <div className="flex items-center gap-2 mb-3">
              <span className="w-5 h-5 rounded-md bg-[#0F2942] text-white flex items-center justify-center text-[11px] font-black">
                7
              </span>
              <h3 className="text-xs font-black text-[#0F2942] uppercase tracking-wider">
                Overall Compliance &amp; Final Disposition Decision
              </h3>
            </div>

            <div className="p-5 rounded-2xl border-2 border-slate-300 bg-gradient-to-r from-slate-50 via-white to-slate-50 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pb-3 border-b border-slate-200 text-xs">
                <div>
                  <span className="text-[10px] font-black text-slate-400 uppercase tracking-wider block">TAC Status</span>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="font-bold text-slate-800">{record.overallCompliance.tacResult}</span>
                    <span className={`px-2 py-0.5 rounded text-[10px] font-black uppercase ${getCompliancePill(record.overallCompliance.tacCompliance)}`}>
                      {record.overallCompliance.tacCompliance}
                    </span>
                  </div>
                </div>

                <div>
                  <span className="text-[10px] font-black text-slate-400 uppercase tracking-wider block">TFC Status</span>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="font-bold text-slate-800">{record.overallCompliance.tfcResult}</span>
                    <span className={`px-2 py-0.5 rounded text-[10px] font-black uppercase ${getCompliancePill(record.overallCompliance.tfcCompliance)}`}>
                      {record.overallCompliance.tfcCompliance}
                    </span>
                  </div>
                </div>

                <div>
                  <span className="text-[10px] font-black text-slate-400 uppercase tracking-wider block">Specified Pathogens</span>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="font-bold text-slate-800">Tested Organisms</span>
                    <span className={`px-2 py-0.5 rounded text-[10px] font-black uppercase ${getCompliancePill(record.overallCompliance.specifiedOrganismsCompliance)}`}>
                      {record.overallCompliance.specifiedOrganismsCompliance}
                    </span>
                  </div>
                </div>
              </div>

              <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 pt-1">
                <div>
                  <span className="text-[10px] font-black text-slate-400 uppercase tracking-wider block">Overall Sample Status</span>
                  <h4 className="text-sm font-black text-slate-900 mt-0.5">
                    {record.overallCompliance.overallSampleStatus}
                  </h4>
                </div>

                <div className="flex items-center gap-3">
                  <span className="text-xs font-black text-slate-500 uppercase tracking-wider">Final Decision:</span>
                  <span className={`px-4 py-2 rounded-xl text-sm font-black uppercase tracking-widest border-2 shadow-xs ${getDecisionBadge(record.overallCompliance.finalDecision)}`}>
                    {record.overallCompliance.finalDecision}
                  </span>
                </div>
              </div>
            </div>
          </section>

          {/* ====================================================================== */}
          {/* SECTION 8: 21 CFR PART 11 ELECTRONIC AUTHORIZATION                     */}
          {/* No handwritten signatures - electronic verification details           */}
          {/* ====================================================================== */}
          <section className="print-break-inside-avoid">
            <div className="flex items-center gap-2 mb-3">
              <span className="w-5 h-5 rounded-md bg-[#0F2942] text-white flex items-center justify-center text-[11px] font-black">
                8
              </span>
              <h3 className="text-xs font-black text-[#0F2942] uppercase tracking-wider">
                21 CFR Part 11 Electronic Signatures &amp; Audit Trail
              </h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Tested By Card */}
              <div className="border border-slate-300 rounded-xl p-4 bg-slate-50/70 space-y-2 text-xs">
                <div className="flex items-center justify-between border-b border-slate-200 pb-1.5">
                  <span className="text-[10px] font-black text-slate-500 uppercase tracking-wider">
                    Tested By (Analyst)
                  </span>
                  <span className={`px-2 py-0.5 rounded text-[9px] font-black uppercase tracking-wider ${
                    record.authorizations.testedBy.status === 'Signed' || record.authorizations.testedBy.status === 'Completed' ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-200 text-slate-600'
                  }`}>
                    {record.authorizations.testedBy.status}
                  </span>
                </div>
                <div className="space-y-0.5">
                  <div className="font-bold text-slate-900">{record.authorizations.testedBy.name}</div>
                  <div className="text-[11px] text-slate-500">{record.authorizations.testedBy.role} (ID: {record.authorizations.testedBy.userId})</div>
                </div>
                <div className="text-[11px] text-slate-700 pt-1">
                  <strong>Action:</strong> {record.authorizations.testedBy.action}
                </div>
                <div className="text-[10px] text-slate-500 font-mono">
                  Timestamp: {record.authorizations.testedBy.timestamp}
                </div>
              </div>

              {/* Checked By Card */}
              <div className="border border-slate-300 rounded-xl p-4 bg-slate-50/70 space-y-2 text-xs">
                <div className="flex items-center justify-between border-b border-slate-200 pb-1.5">
                  <span className="text-[10px] font-black text-slate-500 uppercase tracking-wider">
                    Checked By (Supervisor)
                  </span>
                  <span className={`px-2 py-0.5 rounded text-[9px] font-black uppercase tracking-wider ${
                    record.authorizations.checkedBy.status === 'Signed' || record.authorizations.checkedBy.status === 'Completed' ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'
                  }`}>
                    {record.authorizations.checkedBy.status}
                  </span>
                </div>
                <div className="space-y-0.5">
                  <div className="font-bold text-slate-900">{record.authorizations.checkedBy.name}</div>
                  <div className="text-[11px] text-slate-500">{record.authorizations.checkedBy.role} {record.authorizations.checkedBy.userId !== '-' ? `(ID: ${record.authorizations.checkedBy.userId})` : ''}</div>
                </div>
                <div className="text-[11px] text-slate-700 pt-1">
                  <strong>Action:</strong> {record.authorizations.checkedBy.action}
                </div>
                <div className="text-[10px] text-slate-500 font-mono">
                  Timestamp: {record.authorizations.checkedBy.timestamp}
                </div>
              </div>

              {/* Approved By Card */}
              <div className="border border-slate-300 rounded-xl p-4 bg-slate-50/70 space-y-2 text-xs">
                <div className="flex items-center justify-between border-b border-slate-200 pb-1.5">
                  <span className="text-[10px] font-black text-slate-500 uppercase tracking-wider">
                    Approved By (QA Head / QP)
                  </span>
                  <span className={`px-2 py-0.5 rounded text-[9px] font-black uppercase tracking-wider ${
                    record.authorizations.approvedBy.status === 'Signed' || record.authorizations.approvedBy.status === 'Completed' ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'
                  }`}>
                    {record.authorizations.approvedBy.status}
                  </span>
                </div>
                <div className="space-y-0.5">
                  <div className="font-bold text-slate-900">{record.authorizations.approvedBy.name}</div>
                  <div className="text-[11px] text-slate-500">{record.authorizations.approvedBy.role} {record.authorizations.approvedBy.userId !== '-' ? `(ID: ${record.authorizations.approvedBy.userId})` : ''}</div>
                </div>
                <div className="text-[11px] text-slate-700 pt-1">
                  <strong>Action:</strong> {record.authorizations.approvedBy.action}
                </div>
                <div className="text-[10px] text-slate-500 font-mono">
                  Timestamp: {record.authorizations.approvedBy.timestamp}
                </div>
              </div>
            </div>
          </section>

          {/* Report Footer Note */}
          <div className="pt-6 border-t border-slate-200 text-center text-[10px] text-slate-400 font-mono">
            *** END OF MLT BIOBURDEN ANALYTICAL REPORT • ELECTRONICALLY GENERATED IN ACCORDANCE WITH 21 CFR PART 11 ***
          </div>
        </div>
      </main>
      </div>
    </div>
  );
};

export default MLTBioburdenDetailedReport;
